<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Someone’s hair’s condition and characteristics aren’t the only factors in play when choosing a
      hairstyle to suit. The shape of a person’s face is important too.
    </p>
    <p class="mb-5">
      Take a look at the chart below. It shows the different shapes of people’s faces alongside
      suitable styles of haircut.
    </p>

    <centered-image-wrapper
      :image="require('@/assets/images/experiences/hairdressing/face_shapes.jpg')"
      classes="xl:w-1/2 lg:w-2/3 w-full"
      alt="Chart of face shapes with tips and suitable short, medium and long hairstyles. Heart: show your cheekbones and highlight your eyes; side-swept short bangs, shoulder-length bob, long waves. Square: cover your sharp jawline; rounded bob, volume curls, long curls. Round: contour your cheeks and choose styles that create angles; short layers cutting through cheeks, side-swept long bangs, long layers below shoulder length. Oval: any hairstyle suits this face shape; boy cut, short messy curls, short layers."
    />

    <p class="mb-5">
      For this activity, you’ll need to identify the shapes of 5 faces and choose a haircut that you
      think would best suit them based on the shape of their faces.
    </p>
    <p class="mb-5">
      You can choose from friends, family, or famous people. You’ll need to source a photo of each
      person you choose. You might have pictures of friends and family on your social media accounts
      and, if you chose a famous person, you’ll find lots of photos of them by doing a Google Image
      search.
    </p>
    <p class="mb-5">
      When you’ve selected your five people, follow the steps below for each person:
    </p>

    <p class="mb-5">
      1. Upload the person’s photo/s - preferably a straight-on headshot of the person.
    </p>
    <p class="mb-5">2. Identify the shape of the person’s face from one of the following:</p>
    <ul class="default-list">
      <li>Heart</li>
      <li>Square</li>
      <li>Round</li>
      <li>Oval</li>
    </ul>
    <p class="mb-5">
      3. Choose the hairstyle you think would best suit them from the three available short, medium,
      and long options.
    </p>
    <p class="mb-5">4. Enter the name of the chosen haircut.</p>

    <vs-collapse accordion type="margin" class="p-0">
      <vs-collapse-item
        v-for="index in 5"
        class="faq-bg rounded-lg"
        :class="{ 'mt-0': !index }"
        :key="index"
      >
        <div slot="header">
          <h5>Client {{ index }}</h5>
        </div>
        <div>
          <h5 class="mb-5 questionlabel">1. Upload Image</h5>
          <fileupload-button-immediate
            v-model="answers['uploaded_file_' + index]"
            :is-editable="isEditable"
            filetype="photo"
            :index="index"
          />

          <div v-for="(question, index2) in questions" :key="index2">
            <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
            <div class="vx-row my-5">
              <div class="vx-col w-full mb-2" v-if="isEditable">
                <select
                  v-model="answers['question' + index + '_' + index2]"
                  :aria-label="question.question"
                >
                  <option v-for="answer in question.answers" :key="answer.val" :value="answer.val">
                    {{ answer.answer }}
                  </option>
                </select>
              </div>
              <div class="my-2 mx-5" v-else>
                <p
                  class="my-2"
                  v-for="answer in question.answers"
                  :key="answer.val"
                  v-if="answers['question' + index + '_' + index2] != 0"
                >
                  <span v-if="answers['question' + index + '_' + index2] == answer.val">{{
                    answer.answer
                  }}</span>
                </p>
                <p class="my-2" v-else>N/A</p>
              </div>
            </div>
          </div>

          <h5 class="mb-5 questionlabel">4. Enter the name of the chosen haircut:</h5>
          <div class="vx-row my-5">
            <div class="vx-col w-full mb-2" v-if="isEditable">
              <select v-model="answers['question' + index + '_3']" :aria-label="question.question">
                <optgroup :key="index" v-for="(item, index) in haircut_names" :label="item.name">
                  <option :key="item2" :value="item2" :text="item2" v-for="item2 in item.items">
                    {{ item2 }}
                  </option>
                </optgroup>
              </select>
            </div>
            <p class="my-2 mx-5" v-else>{{ answers['question3' + index + '_3'] }}</p>
          </div>
        </div>
      </vs-collapse-item>
    </vs-collapse>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import VideoTrigger from '@/components/UI/VideoTrigger';
import ResponsiveIframe from '@/components/UI/ResponsiveIframe';
import store from '@/store/store';
import TextareaBlock from '@/views/WorkExperiences/components/ActivitiesBlocks/TextareaBlock';
import TextBlock from '@/views/WorkExperiences/components/ActivitiesBlocks/TextBlock';
import StatusIcon from '@/components/UI/StatusIcon';
export default {
  components: { TextBlock, TextareaBlock, VideoTrigger, ResponsiveIframe, StatusIcon },
  data() {
    return {
      videoTrigger1: require('@/assets/images/experiences/hairdressing/hair_structure_splash.jpg'),
      videos: {
        1:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/hairdressing/hair_structure.mp4',
      },
      login_type: localStorage.getItem('login_type'),
      file: '',
      questions: {
        1: {
          question: '2. Face Shape',
          answers: [
            { val: 1, answer: 'Heart' },
            { val: 2, answer: 'Square' },
            { val: 3, answer: 'Round' },
            { val: 4, answer: 'Oval' },
          ],
        },
        2: {
          question: '3. Choose the hairstyle option',
          answers: [
            { val: 5, answer: 'Short' },
            { val: 6, answer: 'Medium' },
            { val: 7, answer: 'Long' },
          ],
        },
      },
      haircut_names: [
        {
          name: 'Short',
          items: [
            'Side-swept short bangs',
            'Rounded Bob',
            'Short Layers cutting through cheeks',
            'Boy Cut',
          ],
        },
        {
          name: 'Medium',
          items: [
            'Shoulder length Bob',
            'Volume Curls',
            'Side-swept long bangs',
            'Short messy curls',
          ],
        },
        {
          name: 'Long',
          items: ['Long waves', 'Long Curls', 'Long Layers below shoulder length', 'Short Layers'],
        },
      ],
      answers: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
        uploaded_file_4: '',
        uploaded_file_5: '',
        question1_1: null,
        question1_2: null,
        question1_3: null,
        question2_1: null,
        question2_2: null,
        question2_3: null,
        question3_1: null,
        question3_2: null,
        question3_3: null,
        question4_1: null,
        question4_2: null,
        question4_3: null,
        question5_1: null,
        question5_2: null,
        question5_3: null,
      },
      files: {
        file1: '',
        file2: '',
        file3: '',
        file4: '',
        file5: '',
      },
    };
  },
  methods: {
    showVideoPopup(id) {
      store.dispatch(
        'popup/showVideoPopup',
        $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/policing/' + this.videos[id],
      );
    },
    handleFileUpload(index) {
      const file = document.getElementById('file' + index);
      this.files['file' + index] = file.files[0];
    },
    fileButtonClick(index) {
      document.getElementById('file' + index).click();
    },
    closePopup() {
      this.showVideoPopup = false;
    },
    save() {
      this.data['question1'] = this.answers;
      this.saveActivityMultipleFiles(this.$route.params.slug, this.data, this.files);
    },
    deleteFile(index) {
      this.answers['uploaded_file_' + index] = '';
      this.files['file' + index] = '';
    },
  },
  computed: {
    data: {
      get: function () {
        return this.getData();
      },
      set: function (d) {
        this.setData(d);
      },
    },
    uploaded_files: {
      get: function () {
        return this.getUploadedFiles();
      },
      set: function (d) {
        this.setUploadedFiles(d);
      },
    },
  },
  inject: [
    'saveActivityMultipleFiles',
    'getActivityData',
    'getData',
    'setData',
    'getUploadedFiles',
    'setUploadedFiles',
    'isEditable',
    'activityTitle',
  ],
  mounted() {
    if (typeof this.data['question1'] !== 'undefined') this.answers = this.data['question1'];
    for (let i = 1; i <= 5; i++) {
      if (this.uploaded_files !== null && typeof this.uploaded_files[i] !== 'undefined') {
        this.answers['uploaded_file_' + i] = this.uploaded_files[i];
      }
    }
  },
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
}
.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}

.workplace_module {
  overflow: hidden;
  padding: 10px 0;
  border-bottom: 1px solid #dedede;
}
.workplace_module:last-child {
  border-bottom: none;
}
p.module_name {
  float: left;
  font-size: 16px;
  font-weight: 600;
  padding-top: 10px;
}
.prework_icon {
  float: right;
  width: 10%;
  text-align: center;
}
select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
}
</style>
